import React, { useReducer, useImperativeHandle, forwardRef, useCallback, useMemo, useEffect } from 'react';
import { Button, Input, Badge } from 'reactstrap';
import { DATA } from './drillData';
import { nerveAccepts, normNerve, canonRoot, fuzzyHit, fnOptions } from './drillUtils';

// --- Reducer ---
function initState() {
  return DATA.flatMap(sec =>
    sec.rows.map(r => ({
      muscle: r.m,
      nerveAns: r.n,
      rootAns: r.r,
      fnAns: r.f,
      nAcc: nerveAccepts(r.n, r.na),
      nerveValue: '',
      nerveStatus: '', // '', 'ok', 'no', 'rev'
      rootValue: '',
      rootStatus: '',
      fnPicked: null,
      fnCorrect: false,
      fnOptions: fnOptions(r.f),
      fnRevealed: false,
    }))
  );
}

function gradeNerveRow(row) {
  const u = normNerve(row.nerveValue);
  if (!u) return 'rev';
  if (row.nAcc.exact.has(u) || fuzzyHit(u, row.nAcc.fuzzy)) return 'ok';
  return 'no';
}

function gradeRootRow(row) {
  const u = canonRoot(row.rootValue);
  if (!u) return 'rev';
  if (u === canonRoot(row.rootAns)) return 'ok';
  return 'no';
}

function reducer(state, action) {
  switch (action.type) {
    case 'SET_NERVE_VALUE': {
      const s = [...state];
      s[action.idx] = { ...s[action.idx], nerveValue: action.value, nerveStatus: '' };
      return s;
    }
    case 'SET_ROOT_VALUE': {
      const s = [...state];
      s[action.idx] = { ...s[action.idx], rootValue: action.value, rootStatus: '' };
      return s;
    }
    case 'GRADE_NERVE': {
      const s = [...state];
      const row = { ...s[action.idx] };
      row.nerveStatus = gradeNerveRow(row);
      s[action.idx] = row;
      return s;
    }
    case 'GRADE_ROOT': {
      const s = [...state];
      const row = { ...s[action.idx] };
      row.rootStatus = gradeRootRow(row);
      s[action.idx] = row;
      return s;
    }
    case 'PICK_FN': {
      const s = [...state];
      const row = { ...s[action.idx] };
      row.fnPicked = action.value;
      row.fnCorrect = action.value === row.fnAns;
      s[action.idx] = row;
      return s;
    }
    case 'GRADE_ROW':
    case 'GRADE_SECTION':
    case 'CHECK_ALL': {
      const { showNerve, showRoot, showFn } = action;
      const start = action.start || 0;
      const end = action.end || state.length;
      const s = action.type === 'CHECK_ALL' ? state.map(r => ({ ...r })) : [...state];
      for (let i = start; i < end; i++) {
        if (action.type !== 'CHECK_ALL' && i !== action.idx && action.type === 'GRADE_ROW') continue;
        const row = action.type === 'CHECK_ALL' ? s[i] : { ...s[i] };
        if (showNerve) row.nerveStatus = gradeNerveRow(row);
        if (showRoot) row.rootStatus = gradeRootRow(row);
        if (showFn && !row.fnPicked) row.fnRevealed = true;
        s[i] = row;
      }
      return s;
    }
    case 'REVEAL_ALL': {
      const { showNerve, showRoot, showFn } = action;
      return state.map(row => {
        const r = { ...row };
        if (showNerve) r.nerveStatus = 'rev';
        if (showRoot) r.rootStatus = 'rev';
        if (showFn) r.fnRevealed = true;
        return r;
      });
    }
    case 'RESET_ALL':
      return initState();
    default:
      return state;
  }
}

// Map status to Bootstrap-compatible input class
function inputStatusClass(status) {
  if (status === 'ok') return 'is-valid md-ok';
  if (status === 'no') return 'is-invalid md-no';
  if (status === 'rev') return 'md-rev';
  return '';
}

// Map status to answer text color
function answerColorClass(status) {
  if (status === 'ok') return 'text-success';
  if (status === 'no') return 'text-danger';
  if (status === 'rev') return 'text-warning';
  return '';
}

// --- Memoized Row ---
const DrillRow = React.memo(function DrillRow({ row, idx, dispatch, showNerve, showRoot, showFn, gridCols }) {
  const handleNerveChange = useCallback(e => {
    dispatch({ type: 'SET_NERVE_VALUE', idx, value: e.target.value });
  }, [dispatch, idx]);

  const handleRootChange = useCallback(e => {
    dispatch({ type: 'SET_ROOT_VALUE', idx, value: e.target.value });
  }, [dispatch, idx]);

  const handleNerveKey = useCallback(e => {
    if (e.key === 'Enter') { e.preventDefault(); dispatch({ type: 'GRADE_NERVE', idx }); }
  }, [dispatch, idx]);

  const handleRootKey = useCallback(e => {
    if (e.key === 'Enter') { e.preventDefault(); dispatch({ type: 'GRADE_ROOT', idx }); }
  }, [dispatch, idx]);

  const handleRowCheck = useCallback(() => {
    dispatch({ type: 'GRADE_ROW', idx, start: idx, end: idx + 1, showNerve, showRoot, showFn });
  }, [dispatch, idx, showNerve, showRoot, showFn]);

  const handleFnPick = useCallback(val => {
    dispatch({ type: 'PICK_FN', idx, value: val });
  }, [dispatch, idx]);

  const nerveAnswerText = (row.nerveStatus === 'no' || row.nerveStatus === 'rev') ? row.nerveAns : '';
  const rootAnswerText = (row.rootStatus === 'no' || row.rootStatus === 'rev') ? row.rootAns : '';

  return (
    <div className="md-row" style={{ gridTemplateColumns: gridCols }}>
      <div className="md-muscle">
        <div className="d-flex align-items-center justify-content-between">
          <span className="font-weight-bold">{row.muscle}</span>
          <Button color="outline-default" size="sm" className="md-row-check py-0 px-2" onClick={handleRowCheck}>
            Check
          </Button>
        </div>
      </div>
      {showNerve && (
        <div>
          <Input
            bsSize="sm"
            className={inputStatusClass(row.nerveStatus)}
            type="text"
            autoComplete="off"
            spellCheck={false}
            placeholder="nerve"
            value={row.nerveValue}
            onChange={handleNerveChange}
            onKeyDown={handleNerveKey}
          />
          {nerveAnswerText && (
            <small className={`font-weight-bold ${answerColorClass(row.nerveStatus)}`}>
              {nerveAnswerText}
            </small>
          )}
        </div>
      )}
      {showRoot && (
        <div>
          <Input
            bsSize="sm"
            className={`text-monospace ${inputStatusClass(row.rootStatus)}`}
            type="text"
            autoComplete="off"
            spellCheck={false}
            placeholder="root"
            value={row.rootValue}
            onChange={handleRootChange}
            onKeyDown={handleRootKey}
          />
          {rootAnswerText && (
            <small className={`font-weight-bold text-monospace ${answerColorClass(row.rootStatus)}`}>
              {rootAnswerText}
            </small>
          )}
        </div>
      )}
      {showFn && (
        <div className="d-flex flex-column" style={{ gap: '4px' }}>
          {row.fnOptions.map((opt, i) => {
            const locked = !!row.fnPicked || row.fnRevealed;
            let color = 'outline-secondary';
            let extraClass = '';
            if (row.fnPicked) {
              if (opt === row.fnPicked) {
                color = row.fnCorrect ? 'success' : 'danger';
              } else if (opt === row.fnAns) {
                color = 'success';
                extraClass = 'font-weight-bold';
              }
            }
            if (row.fnRevealed && opt === row.fnAns) {
              color = 'success';
              extraClass = 'font-weight-bold';
            }
            return (
              <Button
                key={i}
                size="sm"
                color={color}
                className={`text-left ${extraClass}`}
                style={{ fontSize: '13px', lineHeight: '1.25', pointerEvents: locked ? 'none' : undefined }}
                onClick={() => handleFnPick(opt)}
              >
                {opt}
              </Button>
            );
          })}
        </div>
      )}
    </div>
  );
});

// --- Main Component ---
const MuscleMode = forwardRef(function MuscleMode({ showNerve, showRoot, showFn, onScoreChange }, ref) {
  const [rows, dispatch] = useReducer(reducer, null, initState);

  const sections = useMemo(() => {
    let offset = 0;
    return DATA.map(sec => {
      const start = offset;
      offset += sec.rows.length;
      return { region: sec.region, count: sec.rows.length, start, end: offset };
    });
  }, []);

  const total = rows.length;

  const gridCols = useMemo(() => {
    const cols = ['1.5fr'];
    if (showNerve) cols.push('1.4fr');
    if (showRoot) cols.push('.8fr');
    if (showFn) cols.push('2fr');
    return cols.join(' ');
  }, [showNerve, showRoot, showFn]);

  useEffect(() => {
    let n = 0, r = 0, f = 0;
    rows.forEach(row => {
      if (row.nerveStatus === 'ok') n++;
      if (row.rootStatus === 'ok') r++;
      if (row.fnCorrect) f++;
    });
    onScoreChange({
      nerve: showNerve ? n : 0,
      nerveTotal: showNerve ? total : 0,
      root: showRoot ? r : 0,
      rootTotal: showRoot ? total : 0,
      fn: showFn ? f : 0,
      fnTotal: showFn ? total : 0,
    });
  }, [rows, showNerve, showRoot, showFn, total, onScoreChange]);

  useImperativeHandle(ref, () => ({
    checkAll: () => dispatch({ type: 'CHECK_ALL', showNerve, showRoot, showFn }),
    revealAll: () => dispatch({ type: 'REVEAL_ALL', showNerve, showRoot, showFn }),
    resetAll: () => dispatch({ type: 'RESET_ALL' }),
  }), [showNerve, showRoot, showFn]);

  return (
    <div>
      {sections.map((sec, si) => (
        <section key={si} className="mb-4">
          <div className="md-region-head">
            <h3 className="mb-0 h5 font-weight-bold">{sec.region}</h3>
            <Badge color="light" className="text-muted ml-2">{sec.count}</Badge>
            <Button
              color="outline-default"
              size="sm"
              className="ml-auto md-sec-check"
              onClick={() => dispatch({
                type: 'GRADE_SECTION',
                start: sec.start,
                end: sec.end,
                showNerve, showRoot, showFn
              })}
            >
              Check section
            </Button>
          </div>
          <div className="md-colhead" style={{ gridTemplateColumns: gridCols }}>
            <div>Muscle</div>
            {showNerve && <div>Nerve</div>}
            {showRoot && <div>Root</div>}
            {showFn && <div>Function</div>}
          </div>
          <div className="md-rows">
            {rows.slice(sec.start, sec.end).map((row, ri) => (
              <DrillRow
                key={ri}
                row={row}
                idx={sec.start + ri}
                dispatch={dispatch}
                showNerve={showNerve}
                showRoot={showRoot}
                showFn={showFn}
                gridCols={gridCols}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
});

export default MuscleMode;
